import { useState } from 'react';
import { Modal, Input, Button } from '@react-apps/design-system';
import { useChangePassword } from '../hooks/use-change-password';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Troca de senha in-place (usuário logado). Reautentica com a senha atual no
 * Firebase (prova de posse + renova a recência que o back exige), troca, e o
 * hook refaz o login sozinho — as sessões são revogadas do lado do back.
 */
export function ChangePasswordModal({
  open,
  onClose,
}: ChangePasswordModalProps) {
  const { changePassword, reset, isLoading, error, success } =
    useChangePassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setFormError(null);
    reset(); // limpa success/error do hook para a próxima abertura começar limpa
    onClose();
  };

  const handleSubmit = (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setFormError('A nova senha precisa ter no mínimo 8 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError('As senhas não coincidem.');
      return;
    }
    setFormError(null);
    void changePassword({ currentPassword, newPassword });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={success ? 'Senha alterada' : 'Trocar senha'}
      size="sm"
    >
      {success ? (
        <div className="space-y-4">
          <p className="text-(--color-text-secondary) text-sm">
            Sua senha foi alterada e você foi desconectado dos outros
            dispositivos. Isso pode levar até uma hora para valer em sessões que
            já estavam abertas.
          </p>
          <p className="text-(--color-text-secondary) text-sm">
            Se você estiver logado em outras plataformas Primum,{' '}
            <strong>saia e entre de novo agora</strong> para não ser
            desconectado no meio de uma atividade.
          </p>
          <Button type="button" onClick={handleClose} className="w-full">
            Entendi
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            id="currentPassword"
            label="Senha atual"
            type="password"
            required
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="••••••••"
          />
          <Input
            id="newPassword"
            label="Nova senha"
            type="password"
            required
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="••••••••"
          />
          <Input
            id="confirmPassword"
            label="Confirmar nova senha"
            type="password"
            required
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
          />

          {(formError ?? error) && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
              {formError ?? error}
            </div>
          )}

          <p className="text-(--color-text-secondary) text-xs">
            Por segurança, trocar a senha encerra todas as suas sessões — você
            continua logado aqui, mas precisará entrar de novo nos outros
            dispositivos.
          </p>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                isLoading ||
                !currentPassword ||
                !newPassword ||
                !confirmPassword
              }
            >
              {isLoading ? 'Trocando…' : 'Trocar senha'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
